import type { AppRole } from "@/contexts/RoleContext";

// ─── Types ───────────────────────────────────────────────────────────────────

type FieldPerm = { voir: boolean; modifier: boolean };
type RolePerms = Record<AppRole, FieldPerm>;
export type BienPermissions = Record<string, RolePerms>;

// ─── Field definitions ───────────────────────────────────────────────────────

export const BIEN_SECTIONS = [
  {
    section: "Identification",
    fields: [
      { key: "referenceInterne", label: "Référence interne" },
      { key: "projet", label: "Projet" },
      { key: "referenceExterne", label: "Référence Rentio" },
      { key: "codeImmoweb", label: "Code Immoweb" },
      { key: "typeBien", label: "Type de bien" },
      { key: "transaction", label: "Transaction" },
      { key: "disponibilite", label: "Disponibilité" },
      { key: "sousOption", label: "Sous option" },
      { key: "seraDispoPour", label: "Sera dispo pour" },
      { key: "renonRecuLe", label: "Renon reçu le" },
      { key: "cycleOuvertLe", label: "Cycle ouvert le" },
      { key: "elsRequis", label: "ELS requis" },
      { key: "pebRequis", label: "PEB requis" },
      { key: "finDeContrat", label: "Fin de contrat légal" },
      { key: "collaborateurSrs", label: "Collaborateur SRS" },
      { key: "companyContract", label: "Company contract" },
    ],
  },
  {
    section: "Localisation",
    fields: [
      { key: "adresse", label: "Adresse" },
      { key: "propertyBox", label: "Property Box" },
      { key: "codePostal", label: "Code postal" },
      { key: "ville", label: "Ville" },
      { key: "region", label: "Région" },
      { key: "quartier_id", label: "Quartier" },
      { key: "etage", label: "Étage" },
      { key: "etagesTotal", label: "Étages total" },
      { key: "typeEnvironnement", label: "Type d'environnement" },
    ],
  },
  {
    section: "Intérieur",
    fields: [
      { key: "surfaceHabitable", label: "Surface habitable" },
      { key: "surfaceBrute", label: "Surface brute" },
      { key: "surfaceSalon", label: "Surface salon" },
      { key: "surfaceCuisine", label: "Surface cuisine" },
      { key: "nbChambres", label: "Nb chambres" },
      { key: "nbSdb", label: "Nb SDB" },
      { key: "nbDouches", label: "Nb douches" },
      { key: "nbToilettes", label: "Nb toilettes" },
      { key: "typeSol", label: "Type de sol" },
      { key: "meuble", label: "Meublé" },
      // Répéteur surface + orientation — partagé fiche location ET fiche
      // vente depuis le chantier « fiche en onglets ». `surfacesChambres`
      // reste la colonne miroir lue par le mapper Immoweb.
      { key: "surfacesChambres", label: "Surfaces des chambres (miroir Immoweb)" },
      { key: "chambresDetail", label: "Chambres (surfaces/orientations)" },
    ],
  },
  {
    section: "Équipements",
    fields: [{ key: "equipements", label: "Équipements" }],
  },
  {
    section: "Extérieur",
    fields: [
      { key: "surfaceTerrasse", label: "Surface terrasse (miroir Immoweb)" },
      { key: "terrasses", label: "Terrasses (surfaces/orientations)" },
      { key: "surfaceJardin", label: "Surface jardin" },
      { key: "caveDisponible", label: "Cave disponible" },
      { key: "caveNumero", label: "Cave numéro" },
      { key: "parkingDisponible", label: "Parking disponible" },
      { key: "parkingNumero", label: "Parking numéro" },
      { key: "parkingFerme", label: "Parking fermé" },
      { key: "parkingInterieur", label: "Parking intérieur" },
      { key: "garageDisponible", label: "Garage disponible" },
      { key: "garageNumero", label: "Garage numéro" },
    ],
  },
  {
    section: "Financier",
    fields: [
      { key: "loyerMensuel", label: "Loyer mensuel" },
      { key: "chargesMensuelles", label: "Charges mensuelles" },
      { key: "parkingLoyerMensuel", label: "Parking loyer mensuel" },
      { key: "caveLoyerMensuel", label: "Cave loyer mensuel" },
      { key: "garageLoyerMensuel", label: "Garage loyer mensuel" },
      { key: "chargesIncluses", label: "Charges incluses" },
    ],
  },
  {
    section: "Énergie",
    fields: [
      { key: "classePeb", label: "Classe PEB" },
      { key: "certificatPeb", label: "N° certificat PEB" },
      { key: "pebFinLe", label: "PEB fin le" },
      { key: "consoKwh", label: "Conso kWh/m²/an" },
      { key: "emissionCo2", label: "Émission CO₂" },
      { key: "expertPeb", label: "Expert PEB" },
      { key: "statutPeb", label: "Statut PEB" },
      { key: "nouvClassePeb", label: "Nouvelle classe PEB" },
      { key: "nouvCertificatPeb", label: "Nouveau n° certificat" },
      { key: "nouvPebFinLe", label: "Nouvelle date fin PEB" },
      { key: "nouvConsoKwh", label: "Nouvelle conso" },
      { key: "nouvEmissionCo2", label: "Nouvelle émission" },
      { key: "nouvPebCertificatPdf", label: "Nouveau PDF certificat" },
    ],
  },
  {
    section: "Mission PEB (prestataire)",
    fields: [
      { key: "pebMissionLanceeAt", label: "Mission PEB lancée le" },
      { key: "pebDatePrevue", label: "Date PEB prévue" },
      { key: "pebHeure", label: "Heure PEB" },
      { key: "pebCommentaire", label: "Commentaire PEB" },
      { key: "pebCertificatPdf", label: "PDF certificat PEB" },
    ],
  },
  {
    section: "Construction & Urbanisme",
    fields: [
      { key: "anneeConstruction", label: "Année construction" },
      { key: "anneeRenovation", label: "Année rénovation" },
      { key: "nbFacades", label: "Nb façades" },
      { key: "largeurFacade", label: "Largeur façade (m)" },
      { key: "etatBatiment", label: "État bâtiment" },
    ],
  },
  {
    section: "Locataires",
    fields: [
      { key: "ancienLocataireNom", label: "Ancien locataire — Nom" },
      { key: "ancienLocataireEmail", label: "Ancien locataire — Email" },
      { key: "ancienLocataireTel", label: "Ancien locataire — Tél" },
      { key: "ancienLocataireDateDebut", label: "Ancien locataire — Date début" },
      { key: "ancienLocataireDateFin", label: "Ancien locataire — Date fin" },
      { key: "ancienLocataireLoyerInitial", label: "Ancien locataire — Loyer initial" },
      { key: "ancienLocataireLoyerActuel", label: "Ancien locataire — Loyer actuel" },
      { key: "ancienLocataireChargesInitiales", label: "Ancien locataire — Charges initiales" },
      { key: "ancienLocataireChargesActuelles", label: "Ancien locataire — Charges actuelles" },
      { key: "ancienLocataireIndexBaseYear", label: "Ancien locataire — Année index de base" },
      { key: "nouveauLocataireNom", label: "Nouveau locataire — Nom" },
      { key: "nouveauLocataireEmail", label: "Nouveau locataire — Email" },
      { key: "nouveauLocataireTel", label: "Nouveau locataire — Tél" },
      { key: "nouveauLocataireDateDebut", label: "Nouveau locataire — Date début" },
      { key: "nouveauLocataireDateFin", label: "Nouveau locataire — Date fin" },
      { key: "nouveauLocataireLoyerInitial", label: "Nouveau locataire — Loyer initial" },
      { key: "nouveauLocataireLoyerActuel", label: "Nouveau locataire — Loyer actuel" },
      { key: "nouveauLocataireChargesInitiales", label: "Nouveau locataire — Charges initiales" },
      { key: "nouveauLocataireChargesActuelles", label: "Nouveau locataire — Charges actuelles" },
      { key: "nouveauLocataireIndexBaseYear", label: "Nouveau locataire — Année index de base" },
    ],
  },
  {
    section: "État des lieux",
    fields: [
      // ⚠️ Les colonnes sont nommées à l'envers de leur sens (cf. spec fiche,
      // et `resetElsCycleForLoue` : l'assigné devient le précédent au reset).
      // `prestataireEls` = celui qui a fait l'ELE d'entrée, donc le PRÉCÉDENT ;
      // `prestataireEle` = celui qui fera l'ELS de sortie, donc l'ASSIGNÉ. Les
      // libellés disent le SENS, pas le nom de colonne — sinon cette matrice
      // fait dire à l'admin l'inverse de ce qu'il croit cocher.
      { key: "prestataireEls", label: "Prestataire précédent" },
      { key: "prestataireEle", label: "Prestataire assigné" },
      { key: "elsMissionLanceeAt", label: "Mission ELS lancée le" },
      { key: "edlDatePrevue", label: "Date prévue" },
      { key: "edlHeure", label: "Heure" },
      // Migration 103 : la case écrit `eleNonFait` (colonne réelle, mig. 006).
      // `jeNaiPasFaitEle` était une clé de permission orpheline, sans colonne.
      { key: "eleNonFait", label: "N'a pas fait l'ELE" },
      { key: "transfertElecFait", label: "Transfert élec fait (checkbox)" },
      { key: "transfertElecDate", label: "Transfert élec — Date" },
      // `statutEls` retiré : colonne supprimée (mig. 112), vide sur les 1574
      // biens. La colonne de liste homonyme affiche maintenant le statut
      // CALCULÉ, qui n'est gardé par aucune permission.
      { key: "computedStatut", label: "Statut calculé" },
      { key: "edlCommentaire", label: "Commentaire EDL" },
      // `statutEle` est, malgré son nom, LE statut de l'ELS de sortie : c'est
      // lui que lit `isElsOk()`. La colonne `statutEls`, elle, est vide sur les
      // 1574 biens — plus aucun écrivain depuis la suppression des pages V1.
      { key: "statutEle", label: "Statut de l'ELS" },
      { key: "appartPretPhotos", label: "Appartement prêt pour photos" },
      { key: "dossierCloture", label: "Clôturer le dossier" },
    ],
  },
  {
    section: "Finalisation SRS",
    fields: [
      { key: "indemnitePreavis", label: "Indemnité de préavis" },
      { key: "garantieLiberee", label: "Garantie libérée" },
      { key: "totalSrs", label: "Total SRS" },
      { key: "commentaireSrs", label: "Commentaire SRS" },
      // Les six montants du décompte détaillé ont été retirés de la fiche
      // (décision Thibaud, 2026-08-10) : le décompte se résume au total.
      // Vérifié avant retrait : 0 des 1574 biens portait une valeur.
      // Leur suppression en base est écrite dans la migration 107.
    ],
  },
  {
    section: "Publication",
    fields: [
      { key: "statutPublication", label: "Statut publication" },
      { key: "photosOk", label: "Photos" },
      { key: "videoOk", label: "Vidéo" },
      { key: "textesOk", label: "Textes OK" },
      { key: "alerteTexte", label: "Alerte texte" },
      { key: "titreFr", label: "Titre FR" },
      { key: "descriptionFr", label: "Description FR" },
      { key: "titreNl", label: "Titre NL" },
      { key: "descriptionNl", label: "Description NL" },
      { key: "titreEn", label: "Titre EN" },
      { key: "descriptionEn", label: "Description EN" },
    ],
  },
  {
    section: "Médias",
    fields: [
      { key: "videoYoutubeUrl", label: "Lien vidéo YouTube" },
      { key: "photos", label: "Photos" },
    ],
  },
  {
    section: "Points forts",
    fields: [
      { key: "pointsForts", label: "Points forts" },
    ],
  },
  // ─── Sections fiche VENTE (migration 096 — chantier Catalogue Sales v1) ───
  {
    section: "Vente — Identification",
    fields: [
      { key: "agent", label: "Agent responsable" },
      { key: "evaluationInterne", label: "Évaluation interne" },
      { key: "prix", label: "Prix de vente" },
    ],
  },
  {
    section: "Vente — Général & construction",
    fields: [
      { key: "patrimoineProtege", label: "Patrimoine protégé" },
      { key: "destinationBatiment", label: "Destination du bâtiment" },
      { key: "syndic", label: "Syndic" },
      { key: "partiesCopropriete", label: "Parties en copropriété" },
    ],
  },
  {
    section: "Vente — Intérieur",
    fields: [
      { key: "typeCuisine", label: "Type de cuisine" },
      { key: "salleAManger", label: "Salle à manger" },
      { key: "doubleVitrage", label: "Double vitrage" },
      { key: "chassisMateriau", label: "Châssis — matériau" },
      { key: "chauffageType", label: "Chauffage — type" },
      { key: "chauffageEnergie", label: "Chauffage — énergie" },
    ],
  },
  {
    section: "Vente — Raccordements",
    fields: [
      { key: "egouts", label: "Égouts" },
      { key: "raccordElectricite", label: "Électricité" },
      { key: "raccordGaz", label: "Gaz" },
      { key: "raccordEau", label: "Eau" },
      { key: "citerneMazout", label: "Citerne à mazout" },
      { key: "compteurSepElec", label: "Compteur séparé élec." },
      { key: "compteurSepGaz", label: "Compteur séparé gaz" },
      { key: "compteurSepEau", label: "Compteur séparé eau" },
      { key: "borneVehiculeElec", label: "Borne véhicule élec." },
    ],
  },
  {
    section: "Vente — Finances",
    fields: [
      { key: "prixM2", label: "Prix au m²" },
      { key: "masquerPrix", label: "Masquer le prix" },
      { key: "immeubleRapport", label: "Immeuble de rapport" },
      { key: "revenuCadastral", label: "Revenu cadastral" },
      { key: "rcIndexe", label: "RC indexé" },
      { key: "precompteImmobilier", label: "Précompte immobilier" },
      { key: "sousRegimeTva", label: "Sous régime TVA" },
      { key: "loyerHorsCharges", label: "Loyer hors charges" },
      { key: "dernierLoyer", label: "Dernier loyer précédent" },
      { key: "dernierLoyerDate", label: "Date du dernier loyer" },
    ],
  },
  {
    section: "Vente — Urbanisme & risques",
    fields: [
      { key: "permisConstruire", label: "Permis de construire" },
      { key: "planCadastral", label: "Plan cadastral" },
      { key: "permisLotissement", label: "Permis de lotissement" },
      { key: "droitPreemption", label: "Droit de préemption" },
      { key: "droitPreferenceLocataire", label: "Droit préférence locataire" },
      { key: "infractionUrbanistique", label: "Infraction urbanistique" },
      { key: "destinationUrbanistique", label: "Destination urbanistique" },
      { key: "zoneInondable", label: "Type de zone inondable" },
      { key: "certifInondation", label: "Certif. inondation valable" },
      { key: "pScore", label: "P-score" },
      { key: "gScore", label: "G-score" },
      { key: "registreFlamandConsulte", label: "Registre flamand consulté" },
      { key: "mesureJudiciaire", label: "Mesure judiciaire / admin." },
    ],
  },
  {
    section: "Vente — Extérieur",
    fields: [
      { key: "orientationJardin", label: "Orientation jardin" },
    ],
  },
  {
    section: "Vente — Certificats & amiante",
    fields: [
      { key: "certificatAmiante", label: "Certificat amiante dispo" },
      { key: "construitAvant2001", label: "Construit avant 2001" },
      { key: "certificatAmianteDate", label: "Date certificat amiante" },
      { key: "attestationSol", label: "Attestation du sol" },
    ],
  },
  {
    section: "Vente — Énergie / PEB",
    fields: [
      { key: "consoTheorique", label: "Conso théorique (kWh/an)" },
      { key: "pebMin", label: "PEB min" },
      { key: "pebMax", label: "PEB max" },
      { key: "pebDate", label: "Date du PEB" },
      { key: "inspectionElecValide", label: "Inspection élec. valide" },
      { key: "certifElecDate", label: "Date certif. élec." },
      { key: "renovationObligatoire", label: "Travaux rénovation obligatoire" },
    ],
  },
  {
    section: "Vente — Proximité & disponibilité",
    fields: [
      { key: "proximite", label: "À proximité" },
      { key: "venteDisponibiliteDate", label: "Date de disponibilité (vente)" },
    ],
  },
  {
    section: "Vente — Publication",
    fields: [
      { key: "visibiliteImmoweb", label: "Visibilité Immoweb" },
    ],
  },
];

// ─── Default permissions ─────────────────────────────────────────────────────

export function defaultPerms(): RolePerms {
  return {
    admin:              { voir: true,  modifier: true  },
    c_level:            { voir: true,  modifier: false },
    collaborateur:      { voir: true,  modifier: false },
    collaborateur_els:  { voir: true,  modifier: false },
    collaborateur_afr:  { voir: true,  modifier: false },
    // Équipe Sales : par défaut RIEN (comme prestataire) — seuls les champs
    // de la fiche vente sont ouverts via VENTE_EDIT_FIELDS / VENTE_VIEW_FIELDS
    // (miroir de la migration 097). Les champs location-only (loyers,
    // locataires, ELS, SRS, mailing) restent invisibles.
    vente:              { voir: false, modifier: false },
    publication:        { voir: true,  modifier: false },
    prestataire:        { voir: false, modifier: false },
    autre:              { voir: false, modifier: false },
  };
}

// Fields where publication roles & c_level get edit access by default
const PUBLICATION_EDIT_FIELDS = new Set([
  "statutPublication", "photosOk", "videoOk", "textesOk", "alerteTexte", "titreFr", "descriptionFr", "titreNl", "descriptionNl", "titreEn", "descriptionEn", "pointsForts", "videoYoutubeUrl", "photos",
]);

// Fields hidden from collaborateur/prestataire roles by default
const PUBLICATION_HIDDEN_FOR = new Set(["collaborateur", "collaborateur_els", "collaborateur_afr", "prestataire", "autre"]);

// Champs éditables par le C-Level (persona métier de la sous option : c'est lui
// qui met un bien en négociation hors vitrine — retour user 2026-07-10). Seul le
// booléen a besoin du droit d'écriture : le trigger DB 084 recalcule depuis /
// statutAvant / statutPublication à partir du flip. Miroir DB : migration 086
// (le RPC update_entity_with_perms filtre sur la TABLE field_permissions, pas
// sur ces defaults).
const C_LEVEL_EDIT_FIELDS = new Set(["sousOption"]);

// Champs editables par le prestataire PEB sur sa fiche /audit-peb.
// Spec user 2026-05-23 : rdv (date/heure/commentaire) + donnees PEB
// (classe, certificat, date fin, conso, CO2) + PDF du certificat PEB.
const PRESTATAIRE_PEB_EDIT_FIELDS = new Set([
  "pebDatePrevue", "pebHeure", "pebCommentaire", "pebCertificatPdf",
  "classePeb", "certificatPeb", "pebFinLe", "consoKwh", "emissionCo2",
  "statutPeb", "nouvClassePeb", "nouvCertificatPeb", "nouvPebFinLe",
  "nouvConsoKwh", "nouvEmissionCo2", "nouvPebCertificatPdf",
]);

// ─── Rôle vente (équipe Sales) — miroir de la migration 097 ─────────────────
// Gestion COMPLÈTE de la fiche vente. AUCUN champ location-only (loyers,
// locataires, renon, cycle ELS, SRS) : ils restent sur le défaut voir:false.
const VENTE_EDIT_FIELDS = new Set([
  // Identification
  "referenceInterne", "projet", "referenceExterne", "typeBien",
  "disponibilite", "sousOption", "seraDispoPour", "agent", "evaluationInterne",
  // Général & construction
  "anneeConstruction", "anneeRenovation", "nbFacades", "etatBatiment",
  "patrimoineProtege", "destinationBatiment", "syndic", "partiesCopropriete",
  // Localisation
  "adresse", "propertyBox", "codePostal", "ville", "region", "quartier_id",
  "etage", "etagesTotal", "typeEnvironnement",
  // Intérieur
  "surfaceHabitable", "surfaceBrute", "surfaceSalon", "surfaceCuisine",
  "nbChambres", "nbSdb", "nbDouches", "nbToilettes", "typeSol", "meuble",
  "surfacesChambres", "chambresDetail", "typeCuisine", "salleAManger", "doubleVitrage",
  "chassisMateriau", "chauffageType", "chauffageEnergie",
  // Équipements
  "equipements",
  // Extérieur
  "surfaceTerrasse", "surfaceJardin", "terrasses", "orientationJardin",
  "caveDisponible", "caveNumero", "parkingDisponible", "parkingNumero",
  "parkingFerme", "parkingInterieur", "garageDisponible", "garageNumero",
  // Raccordements
  "egouts", "raccordElectricite", "raccordGaz", "raccordEau", "citerneMazout",
  "compteurSepElec", "compteurSepGaz", "compteurSepEau", "borneVehiculeElec",
  // Finances vente
  "prix", "prixM2", "masquerPrix", "immeubleRapport", "revenuCadastral",
  "rcIndexe", "precompteImmobilier", "sousRegimeTva", "loyerHorsCharges",
  "dernierLoyer", "dernierLoyerDate",
  // Proximité + disponibilité vente
  "proximite", "venteDisponibiliteDate",
  // Urbanisme & risques
  "permisConstruire", "planCadastral", "permisLotissement", "droitPreemption",
  "droitPreferenceLocataire", "infractionUrbanistique",
  "destinationUrbanistique", "zoneInondable", "certifInondation",
  "pScore", "gScore", "registreFlamandConsulte", "mesureJudiciaire",
  // Certificats & amiante
  "certificatAmiante", "construitAvant2001", "certificatAmianteDate",
  "attestationSol",
  // Énergie / PEB (contenu certificat)
  "classePeb", "certificatPeb", "pebFinLe", "consoKwh", "emissionCo2",
  "consoTheorique", "pebMin", "pebMax", "pebDate", "inspectionElecValide",
  "certifElecDate", "renovationObligatoire", "pebCertificatPdf",
  // Publication / vitrine
  "statutPublication", "photosOk", "videoOk", "textesOk", "alerteTexte",
  "titreFr", "descriptionFr", "titreNl", "descriptionNl", "titreEn",
  "descriptionEn", "pointsForts", "videoYoutubeUrl", "photos",
  "visibiliteImmoweb",
]);

// Lecture seule pour la vente : transaction figée à la création, codeImmoweb
// posé par la pipeline, colonnes sous option gérées par le trigger 084.
const VENTE_VIEW_FIELDS = new Set([
  "transaction", "codeImmoweb", "sousOptionDepuis", "sousOptionStatutAvant",
]);

// Champs visibles (lecture seule) par tout prestataire connecte sur sa fiche
// (Identification / Localisation / Ancien locataire / card ELS consultative).
// Sans cet override, defaultPerms() retournerait voir:false pour prestataire et
// les rows ne s afficheraient pas. Cf. cards read-only de EtatDesLieuxDetailPage
// et AuditPebDetailPage.
const PRESTATAIRE_VIEW_FIELDS = new Set([
  "referenceInterne", "projet", "collaborateurSrs", "finDeContrat",
  "adresse", "ville", "codePostal", "etage",
  "ancienLocataireNom", "ancienLocataireEmail", "ancienLocataireTel",
  "edlDatePrevue", "edlHeure", "prestataireEle",
  // expertPeb : critique pour que AuditPebPage puisse filtrer les biens
  // assignes au prestataire PEB courant (sinon la RPC get_entity_for_role
  // strippe la colonne et le filtre client ne matche rien).
  "expertPeb",
]);

export function buildDefaultPermissions(): BienPermissions {
  const perms: BienPermissions = {};
  for (const { fields } of BIEN_SECTIONS) {
    for (const { key } of fields) {
      const p = defaultPerms();
      if (PUBLICATION_EDIT_FIELDS.has(key)) {
        p.publication = { voir: true, modifier: true };
        p.c_level = { voir: true, modifier: true };
      }
      if (PUBLICATION_EDIT_FIELDS.has(key)) {
        for (const r of PUBLICATION_HIDDEN_FOR) {
          p[r as AppRole] = { voir: false, modifier: false };
        }
      }
      if (PRESTATAIRE_PEB_EDIT_FIELDS.has(key)) {
        p.prestataire = { voir: true, modifier: true };
      }
      if (PRESTATAIRE_VIEW_FIELDS.has(key)) {
        p.prestataire = { voir: true, modifier: false };
      }
      if (C_LEVEL_EDIT_FIELDS.has(key)) {
        p.c_level = { voir: true, modifier: true };
      }
      if (VENTE_EDIT_FIELDS.has(key)) {
        p.vente = { voir: true, modifier: true };
      } else if (VENTE_VIEW_FIELDS.has(key)) {
        p.vente = { voir: true, modifier: false };
      }
      perms[key] = p;
    }
  }
  return perms;
}
